import React, { useState } from "react";

const MI_TO_KM = 1.60934;

/** Minutes per unit (mile or km) from "MM:SS" or a decimal minute value. */
function parsePaceMinutesPerUnit(paceRaw) {
  const pace = String(paceRaw ?? "").trim();
  const parts = pace.split(":");

  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    if (
      Number.isNaN(minutes) ||
      Number.isNaN(seconds) ||
      minutes < 0 ||
      seconds < 0 ||
      seconds >= 60
    ) {
      return NaN;
    }
    return minutes + seconds / 60;
  }

  if (parts.length === 1) {
    const n = parseFloat(parts[0]);
    return Number.isNaN(n) || n <= 0 ? NaN : n;
  }

  return NaN;
}

function formatMinutesPerUnitToMMSS(paceMinutes) {
  let mins = Math.floor(paceMinutes);
  let secs = Math.round((paceMinutes - mins) * 60);

  if (secs >= 60) {
    mins += 1;
    secs = 0;
  }

  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function DistanceCalc() {
  const [minutes, setMinutes] = useState("");
  const [pace, setPace] = useState("");
  const [distance, setDistance] = useState("");
  const [unit, setUnit] = useState("miles");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const calculateDistance = (minutesValue, paceValue) => {
    if (!minutesValue || !paceValue) {
      setError("Please enter both time and pace.");
      setDistance("");
      return false;
    }

    const totalMinutes = parseFloat(minutesValue);
    const paceMinutes = parsePaceMinutesPerUnit(paceValue);

    if (
      Number.isNaN(totalMinutes) ||
      totalMinutes <= 0 ||
      Number.isNaN(paceMinutes) ||
      paceMinutes <= 0
    ) {
      setError(
        "Invalid input. Use minutes for time and pace as MM:SS or decimal minutes per unit."
      );
      setDistance("");
      return false;
    }

    const result = totalMinutes / paceMinutes;
    setError("");
    setDistance(result.toFixed(2));
    return true;
  };

  const handleCalculate = () => {
    setSubmitted(true);
    calculateDistance(minutes, pace);
  };

  const handleUnitChange = (e) => {
    const nextUnit = e.target.value;
    let convertedPace = pace;

    if (pace.trim() !== "") {
      const paceMinutes = parsePaceMinutesPerUnit(pace.trim());
      if (!Number.isNaN(paceMinutes) && paceMinutes > 0) {
        const convertedPaceMinutes =
          unit === "miles" && nextUnit === "km"
            ? paceMinutes / MI_TO_KM
            : unit === "km" && nextUnit === "miles"
              ? paceMinutes * MI_TO_KM
              : paceMinutes;

        convertedPace = formatMinutesPerUnitToMMSS(convertedPaceMinutes);
        setPace(convertedPace);
      }
    }

    setUnit(nextUnit);

    // Recalculate immediately only if a result is already visible.
    if (submitted && !error && distance !== "") {
      calculateDistance(minutes, convertedPace);
    }
  };

  const unitWord = unit === "km" ? "km" : "mi";

  return (
    <article className="pw-calc-card">
      <h2 className="pw-calc-card-title">Distance</h2>
      <p className="pw-calc-card-lead">
        Enter how long you run and at what pace to estimate distance covered.
      </p>

      <div className="pw-calc-fields">
        <div className="pw-calc-field">
          <label htmlFor="dist-minutes" className="pw-calc-label">
            Time (minutes)
          </label>
          <input
            id="dist-minutes"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            placeholder="e.g. 45"
            className="pw-calc-input"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </div>
        <div className="pw-calc-field">
          <label htmlFor="dist-pace" className="pw-calc-label">
            Pace (min per unit)
          </label>
          <input
            id="dist-pace"
            type="text"
            placeholder="e.g. 8:51 or 9.25"
            className="pw-calc-input"
            value={pace}
            onChange={(e) => setPace(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="pw-calc-field">
          <label htmlFor="dist-unit" className="pw-calc-label">
            Unit
          </label>
          <select
            id="dist-unit"
            className="pw-calc-select"
            value={unit}
            onChange={handleUnitChange}
          >
            <option value="miles">Miles</option>
            <option value="km">Kilometers</option>
          </select>
        </div>
      </div>

      <p
        style={{
          marginTop: "0.75rem",
          color: "var(--home-muted, #a8a8a8)",
          fontSize: "0.92rem",
          lineHeight: 1.5,
        }}
      >
        Example: You ran for 45 minutes at a 9:00 / mi pace. Enter 45 for time,
        9:00 for pace, select Miles, and hit Calculate. Your estimated distance
        would be 5.00 mi.
      </p>

      <button
        type="button"
        className="pw-calc-submit"
        onClick={handleCalculate}
      >
        Calculate
      </button>

      <div className="pw-calc-results" aria-live="polite">
        {!submitted && (
          <p className="pw-calc-results-placeholder">
            Estimated distance will appear here after you tap Calculate.
          </p>
        )}
        {submitted && error && <p className="pw-calc-results-error">{error}</p>}
        {submitted && !error && distance !== "" && (
          <>
            <span className="pw-calc-results-label">Result</span>
            <p className="pw-calc-results-value">
              Estimated distance:{" "}
              <strong>
                {distance} {unitWord}
              </strong>
            </p>
          </>
        )}
      </div>
    </article>
  );
}
